/* eslint-disable no-unused-vars */
/* eslint-disable no-bitwise */
import { pascalCase } from '@/helpers';
import merge from 'lodash/merge';
import $api from '../../api/index';
import { normalizeRelations, resolveRelations } from '../helpers';

const duration = 10 * 60 * 1000;

const getDefaultState = () => ({
  allIds: [],
  byId: {},
  leftTime: duration
});

const state = getDefaultState();

// Non-relationship fields
const excluded = [
  'fields',
  'id',
  'points_paid',
  'price_paid',
  'current_points',
  'purchased_at',
  'promos',
  'status'
];

const getters = {
  active: (state, getters) => {
    const orderId = state.allIds.find(id => state.byId[id].status === 'draft');
    if (!orderId) return;
    const order = getters.find(orderId);
    return order || false;
  },
  existingOrderItem: (state, getters, rootState, rootGetters) => {
    // If an order item exists get its type
    const itemType = ['deals', 'packages', 'products'].find(
      type => rootState.cart[`cartOrder${pascalCase(type)}`].allIds.length > 0
    );
    // No type, return {}
    // in the view we can check existence with if (existingOrderItem?.itemType) { ... }
    if (!itemType) return false;
    // We have a type, let's use it to get the item
    // since we're only allowing 1 item in the cart it is safe to just grab the first one
    const id = rootState.cart[`cartOrder${pascalCase(itemType)}`].allIds[0];
    return rootGetters[`cartOrder${pascalCase(itemType)}/find`](id);
  },
  find: (state, _, __, rootGetters) => id =>
    resolveRelations(
      state.byId[id],
      [...Object.keys(state.byId[id])].filter(key => !excluded.includes(key)),
      rootGetters,
      {
        inCart: true
      }
    ),
  findOrderItem:
    (state, getters, rootState, rootGetters) => (itemType, itemId) => {
      if (!itemId || !itemType) return false;
      const activeOrder = getters.active;
      if (!activeOrder) return false;
      // gets all deals, products or packages
      const items = activeOrder[`${itemType}s`];
      // find matching item
      const item = items.find(({ id }) => id === itemId);
      if (!item) return false;
      // get the order_TYPE id
      const { id } = item[`order_${itemType}s`][0];
      return rootGetters[`cartOrder${pascalCase(itemType)}s/find`](id);
    },
  getCartLength: (state, getters, rootState) =>
    ['deals', 'packages', 'products'].reduce(
      (acc, type) =>
        acc + rootState.cart[`cartOrder${pascalCase(type)}`].allIds.length,
      0
    ),
  list: (state, getters) => state.allIds.map(id => getters.find(id))
};

const actions = {
  addItemToExistingOrder: async ({ _, dispatch }, payload) => {
    const res = await $api.orders.addItemToExistingOrder(payload);
    if (res.status === 200) dispatch('getOrder', { orderId: payload.orderId });
    return res;
  },

  createOrder: async ({ dispatch }, payload) => {
    const { data } = await $api.orders.createCart(payload);
    await dispatch('getOrder', { orderId: data.orderId });
  },

  deleteItemFromOrder: async ({ commit, dispatch }, payload) => {
    const res = await $api.orders.deleteItemFromOrder(payload);
    if (res.status === 200) {
      // itemType is in the form of order_[TYPE]
      dispatch(
        `cart${pascalCase(payload.itemType)}/remove${pascalCase(
          payload.itemType
        )}`,
        payload.itemId,
        {
          root: true
        }
      );
      commit('cartDistribute/reset', true, { root: true });
    }
    return res;
  },

  getOrder: async ({ commit, dispatch }, payload) => {
    try {
      const { data: order } = await $api.orders.getActiveOrder(payload);

      // Data fields
      const fields = [...Object.keys(order)].filter(
        key => !excluded.includes(key)
      );

      // TODO: Add a deleteRelations() to the helpers so we don't have to do this
      // TOTAL HACK!!! Must be done before committing
      if (!order?.distribute)
        commit('cartDistribute/reset', true, { root: true });

      // Save order with relationships
      commit('add', normalizeRelations(order, fields));

      // Save order data by field (media, venues, categories etc.)
      fields.forEach(field => {
        const addPath = `cart${pascalCase(field)}/add${pascalCase(field)}`;
        dispatch(addPath, order[field], { root: true });
      });
    } catch (error) {
      console.error('Grabbing the active Order failed:', error);
    }
  },

  orderComplete: async ({ commit, dispatch }, payload) => {
    const res = await $api.orders.orderComplete(payload);
    dispatch('resetCart');
    return res;
  },

  orderExpired: async ({ commit, dispatch }, payload) => {
    if (payload.moreTime) {
      await $api.orders.keepOrderAlive(payload);
      commit('resetOrderTimer');
      return;
    }
    // await $api.orders.orderExpired(payload);
    dispatch('deleteItemFromOrder', payload);
    dispatch('resetCart');
  },

  resetCart: ({ commit }) => {
    // Reset order
    commit('reset');

    // Reset everything else
    const resetPaths = [
      'Assignments',
      'Customer',
      'Deals',
      'Distribute',
      'Modules',
      'OrderDeals',
      'OrderPackages',
      'OrderProducts',
      'Packages',
      'Products'
    ];

    resetPaths.forEach(path =>
      commit(`cart${path}/reset`, true, { root: true })
    );
  },

  setShip: ({ commit }, payload) => {
    commit('setShip', payload);
  },

  updateOrder: async ({ _, dispatch }, payload) => {
    const res = await $api.orders.updateOrder(payload);
    if (res.status === 200) dispatch('getOrder', { orderId: payload.orderId });
    return res;
  },

  updateOrderItems: async ({ _, dispatch }, payload) => {
    const res = await $api.orders.updateOrderItems(payload);
    if (res.status === 200) dispatch('getOrder', { orderId: payload.orderId });
  }
};

const mutations = {
  add: (state, order) => {
    if (!state.allIds.includes(order.id)) {
      state.byId[order.id] = order;
      state.allIds.push(order.id);
      return;
    }

    state.byId[order.id] = order;
  },

  setActiveOrderId: (state, payload) => {
    state.activeOrderId = payload.orderId;
    state.orderTime = new Date().getTime() + duration;
  },
  setShip: (state, payload) => {
    state.ship = payload;
  },
  setActiveOrder: (state, payload) => {
    state.activeOrder = payload;
    const { products = [] } = state.activeOrder;
    if (products[0]) {
      const { order_products: orderProducts = [] } = products[0];
      state.orderCount = orderProducts.length;
    } else state.orderCount = 0;
  },
  resetOrderTimer: state => {
    state.leftTime = getDefaultState().leftTime;
  },
  reset: state => Object.assign(state, getDefaultState())
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
